import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatDateTime, formatTime, localized, type StaffMessages } from '@rental/i18n'
import { cn } from '@rental/ui'
import { Link } from '@tanstack/react-router'
import { can } from '../access'
import { useAccess } from '../access-context'
import { type BookingRow, isDone, isLate } from '../board'
import { holdState } from '../hold'
import { useLocale } from '../i18n'
import { ROW, ROW_LINK } from './Results'
import { type ChipTone, StatusChip } from './StatusChip'

/**
 * A booking's status as a chip (DESIGN.md, "A screen and its parts"): on its
 * way or under way is information, a car back is good news, and a booking that
 * has simply ended (closed, cancelled, a no-show) is neutral. Red is kept for
 * something wrong that someone must act on, which no status is by itself.
 */
const STATUS_TONE: Readonly<Record<BookingRow['status'], ChipTone>> = {
  draft: 'neutral',
  confirmed: 'info',
  checked_out: 'info',
  returned: 'success',
  closed: 'neutral',
  cancelled: 'neutral',
  no_show: 'neutral',
}

export function StatusBadge({
  status,
  hold = null,
}: {
  status: BookingRow['status']
  /** W2-S S4: a website draft says where its hold stands instead of "Bozza". */
  hold?: BookingRow['hold']
}) {
  const { messages } = useLocale()
  const held = holdState(hold, new Date())
  if (held) {
    // Waiting on the customer, not on staff: information, then nothing at all.
    return (
      <StatusChip tone={held.kind === 'held' ? 'info' : 'neutral'}>
        {messages.hold[held.kind]}
      </StatusChip>
    )
  }
  return <StatusChip tone={STATUS_TONE[status]}>{messages.status[status]}</StatusChip>
}

export function placeName(place: ApiSchemas['StaffPlace'], locale: 'it' | 'en'): string {
  return localized(place.name, locale)
}

/** A ferry in a row's words: its name, its arrival, or both. */
export function ferryWords(
  ferry: { name: string | null; arrivalAt: string | null },
  locale: 'it' | 'en',
  m: StaffMessages,
): string {
  return [
    ferry.name ? format(m.today.ferry, { name: ferry.name }) : null,
    ferry.arrivalAt
      ? format(m.today.ferryArrival, { time: formatTime(locale, ferry.arrivalAt) })
      : null,
  ]
    .filter(Boolean)
    .join(', ')
}

/**
 * One booking in a list. On the Today board `kind` says which end of the
 * rental the row is about: its time, its place and whether it is late or done.
 * A row of its pane (`ROW`): its code's link covers it, so a finger anywhere
 * on the row opens the booking.
 */
export function BookingItem({
  row,
  kind,
  now,
  when = 'time',
  opens,
}: {
  row: BookingRow
  kind: 'pickup' | 'return'
  now?: string
  when?: 'time' | 'date-time'
  /** S9: whether the code opens the booking, where a trip given to the person decides it (Today). */
  opens?: boolean
}) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const open = opens ?? can(access, 'bookings.read')
  const instant = kind === 'pickup' ? row.startsAt : row.endsAt
  const place = kind === 'pickup' ? row.pickup : row.return
  const late = now ? isLate(row, kind, now) : false
  const done = isDone(row, kind)
  return (
    <li className={cn(ROW, done && 'text-text-muted')}>
      {/* When, who and where it stands first; the status keeps to the right, or to its own line. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="figures font-bold">
          {when === 'time' ? formatTime(locale, instant) : formatDateTime(locale, instant)}
        </span>
        {/* A role that cannot open bookings (the Runner) gets the code, not a dead link. */}
        {open ? (
          <Link
            to="/bookings/$code"
            params={{ code: row.code }}
            className={cn('figures font-bold', ROW_LINK)}
            aria-label={`${row.code}, ${row.customerName}`}
          >
            {row.code}
          </Link>
        ) : (
          <span className="figures font-bold">{row.code}</span>
        )}
        <span className="font-medium">{row.customerName}</span>
        <div className="ms-auto flex flex-wrap gap-2">
          <StatusBadge status={row.status} hold={row.hold} />
          {/* Past its return: red here as in the attention bar, one tone for one state. */}
          {late ? <StatusChip tone="danger">{m.today.late}</StatusChip> : null}
          {row.eligibility !== 'eligible' && !done ? (
            <StatusChip tone={row.eligibility === 'ineligible' ? 'danger' : 'warning'}>
              {m.eligibility[row.eligibility]}
            </StatusChip>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted">
        {/* S9: carried over from the old site, by the reference its customer arrives with. */}
        {row.oldSiteReference ? (
          <span className="wrap-anywhere">
            {format(m.booking.oldSite, { reference: row.oldSiteReference })}
          </span>
        ) : null}
        <span>{localized(row.category.name, locale)}</span>
        {/*
          A zone is a trip: the car goes to the customer, or is fetched at the end. Said in
          the words of the booking's own price lines, so "Consegna" alone never stands next
          to the hand-over's button of the same name, and a return is never called one.
        */}
        <span>
          {place.kind === 'zone'
            ? format(kind === 'pickup' ? m.quoteLines.delivery : m.quoteLines.collection, {
                name: placeName(place, locale),
              })
            : placeName(place, locale)}
        </span>
        <span className="figures">{row.vehicle ? row.vehicle.plate : m.today.unassigned}</span>
        {row.ferry ? <span>{ferryWords(row.ferry, locale, m)}</span> : null}
      </div>
    </li>
  )
}
